import {Component, EventEmitter, Inject, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslationService, validators} from '@emma-labs/shared/angular';
import {
  ENVIRONMENT,
  EnvironmentBroker,
  EXTRA_REQUIREMENTS_TRANSLATIONS,
  ExtraRequirement,
  ExtraRequirementsFile,
  ExtraRequirementsInquiry,
  QuestionnaireId,
  removeNullProperties,
} from '@emma-labs/shared/typescript';
import {ConfirmationService} from 'primeng/api';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {CheckboxModule} from 'primeng/checkbox';
import {DividerModule} from 'primeng/divider';
import {FileRemoveEvent, FileSelectEvent, FileUploadModule} from 'primeng/fileupload';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {BaseSectionComponent} from '../../base-section/base-section.component';
import {ExtraRequirementsInquiriesComponent} from '../extra-requirements-inquiries-cards/extra-requirements-inquiries.component';

type ExtraRequirementsForm = {
  [key in ExtraRequirement]: FormControl<boolean | null>;
} & {
  note: FormControl<string | null>;
  documents: FormControl<File[] | null | undefined>;
  files: FormControl<File[] | null | undefined>;
};

@Component({
  selector: 'em-extra-requirements-section',
  templateUrl: './extra-requirements-section.component.html',
  imports: [
    CheckboxModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    InputTextareaModule,
    FileUploadModule,
    DividerModule,
    ExtraRequirementsInquiriesComponent,
  ],
})
export class ExtraRequirementsSectionComponent extends BaseSectionComponent<
  FormGroup<ExtraRequirementsForm>,
  ExtraRequirementsFile,
  Omit<ExtraRequirementsInquiry, 'id' | 'createdAt'>
> {
  isLoading = false;
  existingExtraRequirementsInquiries: ExtraRequirementsInquiry[] = [];
  existingDocumentNames: string[] = [];
  readonly possibleExtraRequirements: ExtraRequirement[] = Object.values(ExtraRequirement);
  readonly extraRequirementsTranslations = EXTRA_REQUIREMENTS_TRANSLATIONS;

  constructor(
    readonly translate: TranslationService,
    private readonly fb: FormBuilder,
    private readonly confirmationService: ConfirmationService,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);

    this.form = this.createForm();
  }

  @Input({required: true})
  override set props(extraRequirementsFile: ExtraRequirementsFile | undefined) {
    if (extraRequirementsFile?.extraRequirementsInquiries) {
      this.existingExtraRequirementsInquiries = extraRequirementsFile.extraRequirementsInquiries.sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    }

    if (extraRequirementsFile?.documentNames) {
      this.existingDocumentNames = extraRequirementsFile.documentNames;
      this.setDocumentNames();
    }
  }

  @Output() downloadDocument = new EventEmitter<string>();
  @Output() uploadDocuments = new EventEmitter<{
    documents: File[];
    callback: (isSuccess: boolean) => void;
  }>();
  @Output() deleteDocument = new EventEmitter<{
    documentName: string;
    callback: (isSuccess: boolean) => void;
  }>();

  getExtraRequirementFormControl(key: ExtraRequirement): FormControl<QuestionnaireId> {
    return this.form.get(key) as FormControl;
  }

  openSendExtraRequirementsPopup(): void {
    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      header: this.translate.currentLanguageIsFr() ? 'Envoyer les exigences' : 'Send requirements',
      message: this.translate.currentLanguageIsFr()
        ? "Vous êtes sur le point d'envoyer les exigences. Veuillez confirmer en cliquant sur le bouton confirmer."
        : 'You are about to send the requirements. Please confirm by clicking on the confirm button.',
      acceptLabel: this.translate.currentLanguageIsFr() ? 'Confirmer' : 'Confirm',
      rejectLabel: this.translate.currentLanguageIsFr() ? 'Annuler' : 'Cancel',
      rejectButtonStyleClass: 'reject-btn',
      acceptIcon: 'pi',
      rejectIcon: 'pi',
      accept: () => this.submitExtraRequirements(),
    });
  }

  onDownloadDocument(documentName: string) {
    this.downloadDocument.emit(documentName);
  }

  onSelectDocuments($event: FileSelectEvent) {
    const selectedFiles: File[] = Array.from($event.files); // files is of type File[] but it is actually of type FileList if you log it

    this.uploadDocuments.emit({
      documents: selectedFiles,
      callback: (isSuccess) => {
        if (isSuccess) {
          this.form.controls.files.setValue($event.currentFiles, {
            emitEvent: false,
          });
        } else {
          this.form.controls.files.reset(null);
        }
      },
    });
  }

  openRemoveExtraRequirementsPopup($event: FileRemoveEvent) {
    // Reset the form value to the existing document names to counter the fact that PrimeNG removes the file from the UI
    this.setDocumentNames();

    this.confirmationService.confirm({
      icon: 'pi pi-exclamation-triangle',
      header: this.translate.currentLanguageIsFr() ? 'Supprimer le document' : 'Delete document',
      message: this.translate.currentLanguageIsFr()
        ? 'Êtes-vous sûr de vouloir supprimer définitivement ce document?'
        : 'Are you sure you want to permanently delete this document?',
      acceptLabel: this.translate.currentLanguageIsFr() ? 'Confirmer' : 'Confirm',
      rejectLabel: this.translate.currentLanguageIsFr() ? 'Annuler' : 'Cancel',
      rejectButtonStyleClass: 'reject-btn',
      acceptIcon: 'pi',
      rejectIcon: 'pi',
      accept: () => this.onRemoveDocument($event.file.name),
    });
  }

  private setDocumentNames() {
    const files = this.existingDocumentNames.map((name) => new File([], name));
    this.form.controls.files.setValue(files);
  }

  private onRemoveDocument(fileName: string) {
    this.deleteDocument.emit({
      documentName: fileName,
      callback: (status) => {
        if (status) {
          const updatedFiles = this.form.controls.files.value?.filter((file: File) => file.name !== fileName);
          this.form.controls.files.setValue(updatedFiles || null, {emitEvent: false});
        }
      },
    });
  }

  private submitExtraRequirements(): void {
    this.isLoading = true;

    const rawValue = removeNullProperties(this.form.getRawValue());
    const note = rawValue.note.trim();
    delete rawValue.note;
    const selectedExtraRequirements: ExtraRequirement[] = [];
    Object.entries(rawValue).forEach(([extraRequirement, isSelected]) => {
      if (isSelected) {
        selectedExtraRequirements.push(ExtraRequirement[extraRequirement]);
      }
    });

    this.propsChange.emit({
      output: {note, requirements: selectedExtraRequirements},
      callback: (isSuccess) => {
        this.isLoading = false;
        if (isSuccess) {
          this.form.reset();
        }
      },
    });
  }

  private createForm(): FormGroup<ExtraRequirementsForm> {
    return this.fb.group<ExtraRequirementsForm>(
      {
        [ExtraRequirement.BLOOD_PROFILE]: this.fb.control(null),
        [ExtraRequirement.URINE_TEST]: this.fb.control(null),
        [ExtraRequirement.VITAL_SIGNS]: this.fb.control(null),
        [ExtraRequirement.DRIVING_RECORD]: this.fb.control(null),
        [ExtraRequirement.MEDICAL_REPORT]: this.fb.control(null),
        [ExtraRequirement.ECG]: this.fb.control(null),
        [ExtraRequirement.OTHER]: this.fb.control(null),
        note: this.fb.control(null, [validators.notEmpty]),
        documents: this.fb.control(null),
        files: this.fb.control(null),
      },
      {validators: [validators.atLeastOneChecked]},
    );
  }
}
